import React, { useEffect } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import {
    getCalendar,
    updateOpeningHours,
} from 'flow/actions/extension-actions';
import {
    clearCalendarEvents,
    updateCalendar,
} from 'flow/actions/profile-actions';
import CalendarBase from 'flow/components/calendar/CalendarBase';
import Loading from 'flow/components/ui-components/Loading';
import useExtension from 'flow/hooks/useExtension';
import useProfiles from 'flow/hooks/useProfiles';
import useSinglePaneMode from 'flow/hooks/useSinglePaneMode';
import { selectExtensionActiveProfile } from 'flow/selectors/ExtensionSelectors';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { LocationType } from 'flow/types/Location';
import { routes } from 'flow/types/routes';

import { getCalendarWithActiveProfileAddedToEvents } from 'flow/utils/calendar-utils';
import { generatePath } from 'flow/utils/router-utils';

const ChatSessionContactCalendar = () => {
    const dispatch = useAppDispatch();
    const history = useHistory<LocationType>();
    const { pathname } = useLocation();
    const { extensionKey } = useParams<{
        extensionKey: ExtensionKey;
    }>();

    const { extension } = useExtension(extensionKey);
    const { isSinglePaneMode } = useSinglePaneMode();
    const { activeProfiles } = useProfiles(extensionKey);

    const activeProfile = useAppSelector((state) =>
        selectExtensionActiveProfile(state, extensionKey),
    );

    const updatedCalendar = extension.get('calendar')
        ? getCalendarWithActiveProfileAddedToEvents(
              activeProfile,
              extension.get('calendar'),
          )
        : null;

    useEffect(() => {
        dispatch(getCalendar(extensionKey));
    }, [extensionKey]);

    if (!extension.get('calendar')) {
        return <Loading />;
    }

    return (
        <CalendarBase
            calendar={updatedCalendar}
            entityKey={extensionKey}
            profiles={activeProfiles}
            showExternallyManagedInfo={true}
            updateOpeningHours={(openingHours) => {
                dispatch(updateOpeningHours(extensionKey, openingHours));
            }}
            clearCalendar={() => {
                dispatch(clearCalendarEvents(extensionKey));
            }}
            updateCalendar={(updatedCalendar) => {
                dispatch(updateCalendar(extensionKey, updatedCalendar));
            }}
            onClickBack={
                history.location.state?.from
                    ? () =>
                          history.push(history.location.state?.from, {
                              from: pathname,
                          })
                    : isSinglePaneMode
                      ? () =>
                            history.push(
                                generatePath(routes['settings'], {
                                    extensionKey,
                                }),
                            )
                      : undefined
            }
        />
    );
};

export default ChatSessionContactCalendar;
